import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, MapPin, FileText, Download, Eye, LogOut, 
  Loader2, Calendar, Euro, Clock, CheckCircle, AlertCircle,
  Lock, ChevronRight, Settings, Banknote, Wrench, Shield, 
  Plus, MessageSquare, Send, X, Package
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicClientAreaPage() {
  const navigate = useNavigate();
  
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('devis');
  
  // Login/Register state
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [authLoading, setAuthLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  
  // Devis state
  const [devisList, setDevisList] = useState([]);
  const [devisLoading, setDevisLoading] = useState(false);
  
  // SAV state
  const [savRequests, setSavRequests] = useState([]);
  const [savLoading, setSavLoading] = useState(false);
  const [showSavDialog, setShowSavDialog] = useState(false);
  const [savForm, setSavForm] = useState({
    type: 'sav',
    subject: '',
    description: '',
    machine_name: '',
    serial_number: ''
  });
  const [savSubmitting, setSavSubmitting] = useState(false);

  // Check if client is logged in on mount
  useEffect(() => {
    const token = localStorage.getItem('client_token');
    const client = localStorage.getItem('client_data');
    
    if (token && client) {
      setClientData(JSON.parse(client));
      setIsLoggedIn(true);
      fetchClientDevis(token);
      fetchSavRequests(token);
    }
    setLoading(false);
  }, []);

  // Fetch client's devis
  const fetchClientDevis = async (token) => {
    setDevisLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/client/devis`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDevisList(data);
      } else if (response.status === 401) {
        // Token expired
        handleLogout();
      }
    } catch (error) {
      console.error('Failed to fetch devis:', error);
    } finally {
      setDevisLoading(false);
    }
  };

  // Fetch SAV requests
  const fetchSavRequests = async (token) => {
    setSavLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/client/sav-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSavRequests(data);
      }
    } catch (error) {
      console.error('Failed to fetch SAV requests:', error);
    } finally {
      setSavLoading(false);
    }
  };

  // Create SAV request
  const handleCreateSav = async () => {
    if (!savForm.subject || !savForm.description) {
      toast.error('Veuillez remplir le sujet et la description');
      return;
    }
    
    setSavSubmitting(true);
    const token = localStorage.getItem('client_token');
    
    try {
      const response = await fetch(`${API_URL}/api/public/client/sav-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: savForm.type,
          subject: savForm.subject,
          description: savForm.description,
          machine_name: savForm.machine_name,
          serial_number: savForm.serial_number
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la création');
      }
      
      toast.success('Demande créée avec succès !');
      setShowSavDialog(false);
      setSavForm({ type: 'sav', subject: '', description: '', machine_name: '', serial_number: '' });
      fetchSavRequests(token);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavSubmitting(false);
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setAuthLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/client/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur de connexion');
      }
      
      localStorage.setItem('client_token', data.access_token);
      localStorage.setItem('client_data', JSON.stringify(data.client));
      setClientData(data.client);
      setIsLoggedIn(true);
      fetchClientDevis(data.access_token);
      toast.success('Connexion réussie !');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  // Handle register
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerForm.name || !registerForm.email || !registerForm.password) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setAuthLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/client/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: registerForm.name,
          email: registerForm.email,
          phone: registerForm.phone,
          password: registerForm.password
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'inscription');
      }
      
      localStorage.setItem('client_token', data.access_token);
      localStorage.setItem('client_data', JSON.stringify(data.client));
      setClientData(data.client);
      setIsLoggedIn(true);
      toast.success('Compte créé avec succès !');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_data');
    setIsLoggedIn(false);
    setClientData(null);
    setDevisList([]);
    toast.success('Déconnexion réussie');
  };

  // Download devis PDF
  const handleDownloadPDF = async (devisId, reference) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/public/client/devis/${devisId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erreur lors du téléchargement');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Devis_${reference}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Téléchargement démarré');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      brouillon: { label: 'Brouillon', class: 'bg-slate-100 text-slate-700' },
      envoye: { label: 'Envoyé', class: 'bg-blue-100 text-blue-700' },
      en_attente: { label: 'En attente', class: 'bg-yellow-100 text-yellow-700' },
      accepte: { label: 'Accepté', class: 'bg-green-100 text-green-700' },
      refuse: { label: 'Refusé', class: 'bg-red-100 text-red-700' },
      financement_en_cours: { label: 'Financement en cours', class: 'bg-purple-100 text-purple-700' },
      archive: { label: 'Archivé', class: 'bg-slate-100 text-slate-500' },
    };
    const config = statusConfig[status] || { label: status, class: 'bg-slate-100 text-slate-700' };
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  // Not logged in - Show login/register form
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
              <User className="w-8 h-8 text-yellow-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
              ESPACE CLIENT
            </h1>
            <p className="text-slate-600 mt-2">
              Connectez-vous pour accéder à vos devis
            </p>
          </div>

          <Card>
            <CardHeader>
              <Tabs value={authMode} onValueChange={setAuthMode}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login">Connexion</TabsTrigger>
                  <TabsTrigger value="register">Créer un compte</TabsTrigger>
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent>
              {authMode === 'login' ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="votre@email.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      data-testid="client-login-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Mot de passe</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      data-testid="client-login-password"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
                    disabled={authLoading}
                    data-testid="client-login-btn"
                  >
                    {authLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Lock className="w-4 h-4 mr-2" />
                    )}
                    Se connecter
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Nom complet *</Label>
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="Jean Dupont"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                      data-testid="client-register-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email *</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="votre@email.com"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      data-testid="client-register-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-phone">Téléphone</Label>
                    <Input
                      id="register-phone"
                      type="tel"
                      placeholder="0692 XX XX XX"
                      value={registerForm.phone}
                      onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                      data-testid="client-register-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Mot de passe *</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="6 caractères minimum"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      data-testid="client-register-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-confirm">Confirmer le mot de passe *</Label>
                    <Input
                      id="register-confirm"
                      type="password"
                      placeholder="••••••••"
                      value={registerForm.confirmPassword}
                      onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                      data-testid="client-register-confirm"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
                    disabled={authLoading}
                    data-testid="client-register-btn"
                  >
                    {authLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <User className="w-4 h-4 mr-2" />
                    )}
                    Créer mon compte
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-500 mt-6">
            En créant un compte, vous acceptez nos conditions d'utilisation
          </p>
        </div>
      </div>
    );
  }

  // Logged in - Show client dashboard
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
              MON ESPACE CLIENT
            </h1>
            <p className="text-slate-600">Bienvenue, {clientData?.name}</p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="gap-2">
            <LogOut className="w-4 h-4" />
            Déconnexion
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{devisList.length}</p>
                  <p className="text-xs text-slate-500">Devis</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {devisList.filter(d => d.status === 'accepte').length}
                  </p>
                  <p className="text-xs text-slate-500">Garanties</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{savRequests.length}</p>
                  <p className="text-xs text-slate-500">SAV</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Banknote className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {devisList.filter(d => d.financing_request_id).length}
                  </p>
                  <p className="text-xs text-slate-500">Financements</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="devis" className="gap-2">
              <FileText className="w-4 h-4" />
              Mes Devis
            </TabsTrigger>
            <TabsTrigger value="sav" className="gap-2">
              <Wrench className="w-4 h-4" />
              SAV
            </TabsTrigger>
            <TabsTrigger value="garantie" className="gap-2">
              <Shield className="w-4 h-4" />
              Garanties
            </TabsTrigger>
          </TabsList>

          {/* DEVIS TAB */}
          <TabsContent value="devis">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Mes devis</CardTitle>
                  <CardDescription>Retrouvez et téléchargez tous vos devis</CardDescription>
                </div>
                <Link to="/portail/configurateur">
                  <Button className="bg-yellow-500 hover:bg-yellow-600 text-black gap-2">
                    <Plus className="w-4 h-4" />
                    Nouveau devis
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {devisLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
                  </div>
                ) : devisList.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Aucun devis pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {devisList.map((devis) => (
                      <div 
                        key={devis.id} 
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border hover:border-yellow-300 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-semibold text-slate-800">{devis.reference}</span>
                            {getStatusBadge(devis.status)}
                          </div>
                          <p className="text-sm text-slate-600">{devis.mini_pelle_name}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(devis.created_at).toLocaleDateString('fr-FR')}
                            </span>
                            <span className="flex items-center gap-1 font-semibold text-yellow-600">
                              <Euro className="w-3 h-3" />
                              {new Intl.NumberFormat('fr-FR').format(devis.total || 0)} €
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(devis.id, devis.reference)} className="gap-1">
                            <Download className="w-4 h-4" />
                            PDF
                          </Button>
                          {devis.financing_request_id && (
                            <Link to="/portail/financement">
                              <Button variant="outline" size="sm" className="gap-1">
                                <Banknote className="w-4 h-4" />
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SAV TAB */}
          <TabsContent value="sav">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Service Après-Vente</CardTitle>
                  <CardDescription>Signalez un problème ou demandez une intervention</CardDescription>
                </div>
                <Button onClick={() => setShowSavDialog(true)} className="bg-orange-500 hover:bg-orange-600 text-white gap-2">
                  <Plus className="w-4 h-4" />
                  Nouvelle demande SAV
                </Button>
              </CardHeader>
              <CardContent>
                {savLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                  </div>
                ) : savRequests.length === 0 ? (
                  <div className="text-center py-8">
                    <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Aucune demande SAV</p>
                    <p className="text-sm text-slate-400 mt-1">Vous n'avez pas encore fait de demande d'intervention</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {savRequests.map((sav) => (
                      <div 
                        key={sav.id} 
                        className="p-4 bg-slate-50 rounded-lg border"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-slate-800">SAV-{sav.id?.slice(0, 8).toUpperCase()}</span>
                              <Badge className={
                                sav.status === 'resolu' ? 'bg-green-100 text-green-700' :
                                sav.status === 'en_cours' ? 'bg-blue-100 text-blue-700' :
                                sav.status === 'planifie' ? 'bg-purple-100 text-purple-700' :
                                'bg-yellow-100 text-yellow-700'
                              }>
                                {sav.status === 'resolu' ? 'Résolu' :
                                 sav.status === 'en_cours' ? 'En cours' :
                                 sav.status === 'planifie' ? 'Planifié' : 'En attente'}
                              </Badge>
                              <Badge variant="outline" className={
                                sav.urgency === 'urgente' ? 'border-red-300 text-red-600' :
                                sav.urgency === 'haute' ? 'border-orange-300 text-orange-600' :
                                'border-slate-300 text-slate-600'
                              }>
                                {sav.urgency === 'urgente' ? 'Urgent' :
                                 sav.urgency === 'haute' ? 'Prioritaire' : 'Normal'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600 mb-2">{sav.description}</p>
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Package className="w-3 h-3" />
                                {sav.devis_reference || 'N/A'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(sav.created_at).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* GARANTIES TAB */}
          <TabsContent value="garantie">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Mes Garanties</CardTitle>
                <CardDescription>Consultez les garanties de vos équipements</CardDescription>
              </CardHeader>
              <CardContent>
                {devisList.filter(d => d.status === 'accepte').length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Aucune garantie active</p>
                    <p className="text-sm text-slate-400 mt-1">Les garanties s'activent après validation de votre commande</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {devisList.filter(d => d.status === 'accepte').map((devis) => {
                      // Calculate warranty end date (2 years from acceptance)
                      const startDate = new Date(devis.updated_at || devis.created_at);
                      const endDate = new Date(startDate);
                      endDate.setFullYear(endDate.getFullYear() + 2);
                      const isExpired = new Date() > endDate;
                      const daysLeft = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
                      
                      return (
                        <div 
                          key={devis.id} 
                          className={`p-5 rounded-xl border-2 ${isExpired ? 'border-slate-200 bg-slate-50' : 'border-green-200 bg-green-50'}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isExpired ? 'bg-slate-200' : 'bg-green-100'}`}>
                                <Shield className={`w-6 h-6 ${isExpired ? 'text-slate-500' : 'text-green-600'}`} />
                              </div>
                              <div>
                                <h3 className="font-semibold text-slate-800">{devis.mini_pelle_name}</h3>
                                <p className="text-sm text-slate-500">{devis.reference}</p>
                                <div className="mt-2 flex items-center gap-4 text-sm">
                                  <span className="text-slate-600">
                                    <strong>Début :</strong> {startDate.toLocaleDateString('fr-FR')}
                                  </span>
                                  <span className="text-slate-600">
                                    <strong>Fin :</strong> {endDate.toLocaleDateString('fr-FR')}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              {isExpired ? (
                                <Badge className="bg-slate-200 text-slate-600">Expirée</Badge>
                              ) : (
                                <>
                                  <Badge className="bg-green-100 text-green-700">Active</Badge>
                                  <p className="text-xs text-green-600 mt-1">{daysLeft} jours restants</p>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="mt-4 pt-4 border-t border-green-200 grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Type de garantie</p>
                              <p className="font-medium">Garantie constructeur 2 ans</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Couverture</p>
                              <p className="font-medium">Pièces et main d'œuvre</p>
                            </div>
                          </div>
                          {!isExpired && (
                            <div className="mt-4">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="gap-2"
                                onClick={() => {
                                  setSavForm({ ...savForm, devis_id: devis.id });
                                  setShowSavDialog(true);
                                  setActiveTab('sav');
                                }}
                              >
                                <Wrench className="w-4 h-4" />
                                Faire une demande SAV
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* SAV Dialog */}
        <Dialog open={showSavDialog} onOpenChange={setShowSavDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-orange-500" />
                Nouvelle demande
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Type de demande *</Label>
                <Select value={savForm.type} onValueChange={(v) => setSavForm({ ...savForm, type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sav">SAV / Panne</SelectItem>
                    <SelectItem value="garantie">Garantie</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Sujet *</Label>
                <Input
                  value={savForm.subject}
                  onChange={(e) => setSavForm({ ...savForm, subject: e.target.value })}
                  placeholder="Ex: Problème de démarrage"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Machine concernée</Label>
                <Input
                  value={savForm.machine_name}
                  onChange={(e) => setSavForm({ ...savForm, machine_name: e.target.value })}
                  placeholder="Ex: Kubota K008-3"
                />
              </div>
              
              <div className="space-y-2">
                <Label>N° de série (si connu)</Label>
                <Input
                  value={savForm.serial_number}
                  onChange={(e) => setSavForm({ ...savForm, serial_number: e.target.value })}
                  placeholder="Ex: KUB-2024-XXXX"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={savForm.description}
                  onChange={(e) => setSavForm({ ...savForm, description: e.target.value })}
                  placeholder="Décrivez votre problème en détail..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSavDialog(false)}>Annuler</Button>
              <Button 
                onClick={handleCreateSav} 
                disabled={savSubmitting || !savForm.subject || !savForm.description}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {savSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
